import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AdminBrandDataSourceService } from '../../services/admin-brand-data-source.service';
import { HttpErrorService } from '@web/common';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { Guid, once } from '@web/utils';
import { HttpErrorResponse } from '@angular/common/http';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, switchMap } from 'rxjs';
import { AdminBrandDetailsForm } from '../../models/admin-brand-details-form';
import { AdminBrandDetailsFormComponent } from '../../components/admin-brand-details-form/admin-brand-details-form.component';
import { SummernoteImageUpload } from '@web/shared/external';
import { AdminBrandBannerManagementComponent } from '../admin-brand-banner-management/admin-brand-banner-management.component';

@Component({
  selector: 'app-admin-brand-edit',
  imports: [
    AdminBrandBannerManagementComponent,
    AdminBrandDetailsFormComponent,
    AlertsComponent,
    CommonModule,
    SpinnerFilledComponent,
  ],
  templateUrl: './admin-brand-edit.component.html',
  styleUrl: './admin-brand-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class AdminBrandEditComponent {
  private readonly brandDataSource = inject(AdminBrandDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);

  public readonly id = input.required<Guid>();
  public readonly submitSuccessEvent = output<void>();

  protected readonly id$ = toObservable(this.id);
  protected readonly form$ = this.formSource();

  public handleSubmitClickEvent(data: AdminBrandDetailsForm): void {
    this.brandDataSource
      .updateByFormData(this.id(), data)
      .pipe(once())
      .subscribe({
        next: () => this.notifySubmitSuccessEvent(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  protected readonly uploadFn = (
    files: FileList,
  ): Observable<SummernoteImageUpload[]> => {
    return this.brandDataSource.uploadEditorImages(Array.from(files));
  };

  protected handleBannerChangedEvent(): void {
    // Banner management lives inside brand edit now, so no extra action is needed here.
  }

  private notifySubmitSuccessEvent(): void {
    this.submitSuccessEvent.emit();
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }

  private formSource(): Observable<AdminBrandDetailsForm> {
    return this.id$.pipe(
      switchMap((id) => this.brandDataSource.fetchDetailsFormById(id)),
    );
  }
}
